// Welcome to ServiceNow - Part 2: Analytics through Course Summary
// Topics: Platform Analytics, Knowledge Management, Service Catalog, Virtual Agent, Course Summary

export const welcomePart2Topics = [
  {
    id: 'w-analytics',
    title: 'Platform Analytics',
    readingTime: 20,
    subtopics: [
      {
        id: 'w-analytics-lesson',
        title: 'Platform Analytics Lesson',
        readingTime: 12,
        content: `<h2>Platform Analytics in ServiceNow</h2>
<p>ServiceNow Platform Analytics provides powerful reporting, dashboarding, and data visualization capabilities that enable organizations to gain actionable insights from their data, track key performance indicators (KPIs), identify trends, and make data-driven decisions. As an administrator, you'll create reports and dashboards for various stakeholders — from C-level executives needing strategic overviews to frontline agents needing operational metrics.</p>

<h3>Reporting Fundamentals</h3>
<p>ServiceNow reports pull data from any table in the platform and present it in various visual formats. Understanding the reporting framework is essential for effective administration.</p>

<p><strong>Report Types:</strong></p>
<ul>
<li><strong>Bar Chart:</strong> Compares values across categories. Ideal for showing incident counts by category, group, or priority. Supports horizontal and stacked variations.</li>
<li><strong>Pie / Donut Chart:</strong> Shows proportional distribution. Best for displaying the percentage breakdown of incidents by state, priority distribution, or request types.</li>
<li><strong>Line Chart / Time Series:</strong> Tracks values over time. Essential for trending analysis — tracking incident volumes by week, SLA compliance rates by month, or resolution times over quarters.</li>
<li><strong>Pivot Table:</strong> Cross-tabulates data in a matrix format. Shows incident counts broken down by both Priority (rows) and Category (columns), for example.</li>
<li><strong>List Report:</strong> Displays records in a tabular list format with selected columns. Useful for detail-level reports and data exports.</li>
<li><strong>Single Score / Dial:</strong> Shows a single numeric value or gauge. Perfect for dashboard KPI widgets (e.g., "Total Open Incidents: 234" or "SLA Compliance: 94%").</li>
<li><strong>Heatmap:</strong> Color-coded matrix showing data density. Useful for identifying patterns in time-based data.</li>
<li><strong>Calendar Report:</strong> Displays records on a calendar view based on date fields.</li>
</ul>

<p><strong>Creating a Report</strong></p>
<p>Reports are created using the <strong>Report Builder</strong>, accessible via <strong>Reports > Create New</strong>. The builder walks you through a step-by-step process:</p>
<ol>
<li><strong>Data:</strong> Select the source table and add filter conditions to narrow the data set</li>
<li><strong>Type:</strong> Choose the visualization type (bar, pie, line, etc.)</li>
<li><strong>Configure:</strong> Set the Group By field (X-axis), aggregation (Count, Sum, Average), and any secondary grouping (Stack By)</li>
<li><strong>Style:</strong> Customize colors, labels, legends, and formatting options</li>
</ol>

<h3>Dashboards</h3>
<p>Dashboards combine multiple reports, visualizations, and widgets into a single, unified view. They serve as the primary data consumption tool for most users.</p>

<p><strong>Dashboard Components:</strong></p>
<ul>
<li><strong>Reports:</strong> Any saved report can be added as a dashboard widget</li>
<li><strong>Performance Analytics Widgets:</strong> KPI scorecards, breakdowns, and trend indicators</li>
<li><strong>Custom Widgets:</strong> HTML, Angular-based, or Now Experience widgets for custom content</li>
<li><strong>List Widgets:</strong> Embedded list views showing specific filtered records</li>
</ul>

<p><strong>Dashboard Configuration:</strong></p>
<ul>
<li>Dashboards are organized into <strong>tabs</strong> — each tab can have its own layout and widgets</li>
<li>Widgets can be arranged in configurable grid layouts with drag-and-drop positioning</li>
<li>Dashboard sharing can be controlled: make dashboards personal, shared with specific groups, or published to all users</li>
<li>Interactive dashboards support <strong>drill-down</strong> — clicking a chart element navigates to the underlying records</li>
</ul>

<h3>Performance Analytics (PA)</h3>
<p>Performance Analytics extends basic reporting with enterprise-grade analytics capabilities:</p>
<ul>
<li><strong>Indicators:</strong> Defined metrics that are collected over time (e.g., "Average Incident Resolution Time," "Open Incident Count"). Indicators create historical data points that enable trend analysis.</li>
<li><strong>Breakdowns:</strong> Slice indicator data by dimensions like priority, category, assignment group, or location to identify patterns and outliers</li>
<li><strong>Targets:</strong> Set goals for indicators (e.g., "SLA Compliance should be above 95%") and track performance against them with visual indicators</li>
<li><strong>Scorecards:</strong> Combine multiple indicators into a comprehensive performance summary for a team, process, or service</li>
<li><strong>Analytics Center:</strong> A centralized hub for exploring data with natural language queries, creating ad-hoc visualizations, and discovering insights</li>
</ul>

<h3>Predictive Intelligence</h3>
<p>ServiceNow's machine learning capabilities add predictive analytics on top of historical reporting:</p>
<ul>
<li><strong>Predictive Models:</strong> Forecast future metric values based on historical trends</li>
<li><strong>Anomaly Detection:</strong> Automatically identify unusual patterns in data that may indicate emerging issues</li>
<li><strong>Classification:</strong> Auto-categorize and prioritize incoming records based on patterns learned from historical data</li>
<li><strong>Similarity:</strong> Identify similar past records to assist with current issues (e.g., finding similar past incidents for faster resolution)</li>
</ul>

<h3>Best Practices for Analytics</h3>
<ul>
<li><strong>Design for the audience:</strong> Executive dashboards should show high-level KPIs with trend indicators. Manager dashboards should show team performance with drill-down capability. Agent dashboards should show personal queue and actionable items.</li>
<li><strong>Keep it focused:</strong> Each dashboard should answer specific questions. Avoid cramming too many widgets into a single view.</li>
<li><strong>Use consistent metrics:</strong> Standardize how metrics are calculated across the organization to avoid conflicting data.</li>
<li><strong>Schedule report distribution:</strong> Set up automated report delivery via email so stakeholders receive regular updates without visiting the platform.</li>
</ul>`
      },
      {
        id: 'w-analytics-task',
        title: 'Platform Analytics Task',
        readingTime: 8,
        content: `<h2>Hands-On: Platform Analytics</h2>
<p>This exercise teaches you how to create reports, build dashboards, and work with the analytics tools in ServiceNow.</p>

<h3>Exercise 1: Create a Bar Chart Report</h3>
<ol>
<li>Navigate to <strong>Reports > Create New</strong></li>
<li><strong>Data step:</strong> Select the <strong>Incident</strong> table as the data source</li>
<li>Add a filter condition: <strong>State is not Closed</strong> (to focus on active incidents)</li>
<li><strong>Type step:</strong> Select <strong>Bar Chart</strong></li>
<li><strong>Configure step:</strong> Set Group By to <strong>"Priority"</strong> and Aggregation to <strong>"Count"</strong></li>
<li><strong>Style step:</strong> Set the chart title to "Open Incidents by Priority" and choose a color scheme</li>
<li>Click <strong>"Save"</strong> and name it "Open Incidents by Priority"</li>
<li>Click <strong>"Run"</strong> to preview the report</li>
</ol>

<h3>Exercise 2: Create a Trend Report</h3>
<ol>
<li>Create another new report on the Incident table</li>
<li>Select <strong>Line Chart</strong> type</li>
<li>Configure: Group By = <strong>"Opened at"</strong>, set the trend interval to <strong>"Week"</strong></li>
<li>This creates a line chart showing incident creation trends over time</li>
<li>Add a secondary grouping: Stack By = <strong>"Priority"</strong> to see how each priority level contributes to the overall trend</li>
<li>Save as "Weekly Incident Trend by Priority"</li>
</ol>

<h3>Exercise 3: Create a Pivot Table</h3>
<ol>
<li>Create a new report on Incidents with type <strong>Pivot Table</strong></li>
<li>Row Group By: <strong>"Category"</strong></li>
<li>Column Group By: <strong>"Priority"</strong></li>
<li>Aggregation: <strong>"Count"</strong></li>
<li>This creates a cross-tabulation showing how many incidents exist for each Category/Priority combination</li>
<li>Save as "Incidents: Category vs Priority Matrix"</li>
</ol>

<h3>Exercise 4: Build a Dashboard</h3>
<ol>
<li>Navigate to <strong>Self-Service > Dashboards</strong> (or PA Dashboards)</li>
<li>Click <strong>"New"</strong> to create a new dashboard</li>
<li>Name it "IT Service Management Overview"</li>
<li>Add a new <strong>tab</strong> called "Incidents"</li>
<li>Click <strong>"Add Widget"</strong> and select <strong>"Report"</strong></li>
<li>Search for and add the "Open Incidents by Priority" report you created</li>
<li>Repeat to add the "Weekly Incident Trend" and "Category vs Priority" reports</li>
<li>Arrange the widgets in a logical layout using drag-and-drop</li>
<li>Add a <strong>Single Score</strong> widget: configure it to show the total count of open incidents</li>
</ol>

<h3>Exercise 5: Schedule a Report</h3>
<ol>
<li>Open one of your saved reports</li>
<li>Click the <strong>"Sharing"</strong> or <strong>"Schedule"</strong> option</li>
<li>Configure a schedule: send the report via email every Monday at 8:00 AM</li>
<li>Select the format (PDF, Excel, or HTML)</li>
<li>Add recipients (your email address for testing)</li>
<li>Save the schedule</li>
</ol>

<h3>Key Takeaways</h3>
<p>Reporting and analytics are critical administrative skills. Key points: (1) always start with the business question you want to answer, then choose the appropriate report type, (2) use filters to ensure reports show only relevant data, (3) dashboards should be designed for specific audiences with appropriate drill-down capability, (4) schedule regular report distribution to keep stakeholders informed automatically, and (5) Performance Analytics provides time-series data collection for trend analysis that basic reports cannot offer.</p>`
      }
    ]
  },
  {
    id: 'w-knowledge',
    title: 'Knowledge Management',
    readingTime: 20,
    subtopics: [
      {
        id: 'w-knowledge-lesson',
        title: 'Knowledge Management Lesson',
        readingTime: 12,
        content: `<h2>Knowledge Management in ServiceNow</h2>
<p>ServiceNow Knowledge Management enables organizations to create, organize, share, and maintain a centralized repository of knowledge articles. An effective knowledge base is one of the highest-value assets for any IT organization — it reduces ticket volumes through self-service resolution, accelerates agent-assisted resolution by providing quick access to proven solutions, preserves institutional knowledge when employees leave, and ensures consistent service delivery across the organization.</p>

<h3>Knowledge Management Architecture</h3>
<p>The knowledge management system in ServiceNow is organized in a hierarchical structure:</p>
<ul>
<li><strong>Knowledge Bases:</strong> Top-level containers that organize articles by department, audience, or subject area. Examples: "IT Knowledge Base," "HR Knowledge Base," "Customer-Facing Knowledge Base." Each knowledge base has its own owners, managers, and visibility settings.</li>
<li><strong>Categories:</strong> Hierarchical folders within a knowledge base for logical organization. Categories can be nested (parent/child) to create a taxonomy. Example: IT Knowledge Base > Hardware > Printers > Troubleshooting.</li>
<li><strong>Articles [kb_knowledge]:</strong> Individual knowledge documents containing solutions, procedures, FAQs, or reference information. Each article belongs to a knowledge base, can be tagged with categories, and follows a configurable lifecycle.</li>
</ul>

<h3>Article Lifecycle</h3>
<p>Knowledge articles follow a configurable workflow that ensures quality and accuracy:</p>
<ol>
<li><strong>Draft:</strong> Initial state when an article is created. Only the author and designated reviewers can see it. The author writes and refines the content.</li>
<li><strong>Review:</strong> Article is submitted for peer or manager review. Reviewers check accuracy, completeness, formatting, and adherence to knowledge standards.</li>
<li><strong>Published:</strong> Article passes review and becomes visible to its intended audience. Published articles appear in search results, Service Portal, and agent-facing knowledge views.</li>
<li><strong>Scheduled Review:</strong> Articles can be configured for periodic review to ensure content remains current. The system automatically notifies the knowledge owner when a review is due.</li>
<li><strong>Retired:</strong> Outdated or obsolete articles are retired. They are removed from search results and public views but remain in the database for historical reference.</li>
</ol>

<h3>Creating Effective Knowledge Articles</h3>
<p>Well-written knowledge articles are the foundation of an effective knowledge base. Best practices include:</p>

<p><strong>Article Structure:</strong></p>
<ul>
<li><strong>Title:</strong> Clear, descriptive, and searchable. Include key terms users would search for (e.g., "How to Reset Your Password Using Self-Service Portal" not "Password Procedure")</li>
<li><strong>Symptoms / Problem Statement:</strong> Describe the issue the reader is experiencing. This helps match the article to the user's situation.</li>
<li><strong>Cause:</strong> Explain why the issue occurs (when applicable). Understanding the cause helps readers determine if the article applies to their situation.</li>
<li><strong>Resolution / Steps:</strong> Clear, numbered steps to resolve the issue. Include screenshots where helpful. Each step should describe one action.</li>
<li><strong>Additional Information:</strong> Related articles, escalation contacts, or supplementary resources</li>
</ul>

<p><strong>Writing Tips:</strong></p>
<ul>
<li>Write for your target audience — end-user articles should be non-technical; agent articles can include technical detail</li>
<li>Use consistent formatting and terminology across all articles</li>
<li>Include screenshots and diagrams for complex procedures</li>
<li>Add relevant keywords and meta tags to improve searchability</li>
<li>Keep articles focused on a single topic — write multiple articles rather than one long, multi-topic document</li>
</ul>

<h3>Knowledge Search and Discovery</h3>
<p>ServiceNow provides multiple ways for users to find knowledge articles:</p>
<ul>
<li><strong>Full-Text Search:</strong> Searches across article titles, body content, keywords, and metadata with relevance ranking</li>
<li><strong>Contextual Knowledge:</strong> When agents view incidents or cases, ServiceNow can automatically suggest relevant articles based on the record's category, short description, or other fields using AI-powered matching</li>
<li><strong>Service Portal Knowledge Widget:</strong> End users can browse and search knowledge directly from the self-service portal</li>
<li><strong>Virtual Agent Integration:</strong> Virtual Agent can search and present knowledge articles in conversational responses</li>
<li><strong>Knowledge Blocks:</strong> Reusable content snippets that can be embedded across multiple articles, ensuring consistency when the same information needs to appear in multiple places</li>
</ul>

<h3>Knowledge Metrics and Improvement</h3>
<p>Track these key metrics to measure and improve your knowledge base effectiveness:</p>
<ul>
<li><strong>Article Usage:</strong> How many times each article is viewed and by whom</li>
<li><strong>Feedback Ratings:</strong> User ratings and comments on article helpfulness</li>
<li><strong>Attachment Rate:</strong> How often articles are attached to incidents as the resolution</li>
<li><strong>Deflection Rate:</strong> How many potential tickets were prevented because users found answers in knowledge articles</li>
<li><strong>Age and Staleness:</strong> How old articles are and whether they've been reviewed recently</li>
</ul>

<h3>Knowledge-Centered Service (KCS)</h3>
<p>ServiceNow supports KCS methodology, which integrates knowledge creation into the incident resolution process. Rather than writing articles as a separate activity, KCS encourages agents to create or update knowledge articles as part of solving each issue. This "solve it once, share it many times" approach rapidly builds a comprehensive and relevant knowledge base.</p>`
      },
      {
        id: 'w-knowledge-task',
        title: 'Knowledge Base Submission Task',
        readingTime: 8,
        content: `<h2>Hands-On: Knowledge Base Submission</h2>
<p>This exercise walks you through creating a knowledge base, building categories, writing a knowledge article, and managing the article lifecycle from creation through publication.</p>

<h3>Exercise 1: Create a Knowledge Base</h3>
<ol>
<li>Navigate to <strong>Knowledge > Knowledge Bases</strong></li>
<li>Click <strong>"New"</strong> to create a new knowledge base</li>
<li>Fill in the following fields:
  <ul>
    <li><strong>Title:</strong> "IT Support Knowledge Base"</li>
    <li><strong>Description:</strong> "Central repository for IT support solutions, procedures, and FAQs"</li>
    <li><strong>Owner:</strong> Select yourself</li>
    <li><strong>Managers:</strong> Add yourself as a manager (this role can approve articles)</li>
  </ul>
</li>
<li>In the <strong>"Visibility"</strong> section, configure who can view articles in this knowledge base. For testing, set it to "All users."</li>
<li>Click <strong>"Submit"</strong> to create the knowledge base</li>
</ol>

<h3>Exercise 2: Set Up Categories</h3>
<ol>
<li>Open your newly created knowledge base</li>
<li>Scroll down to the <strong>"Knowledge Base Categories"</strong> related list</li>
<li>Click <strong>"New"</strong> to create categories:
  <ul>
    <li>Category 1: "How-To Guides" — For step-by-step procedures</li>
    <li>Category 2: "Troubleshooting" — For problem/solution articles</li>
    <li>Category 3: "FAQs" — For frequently asked questions</li>
    <li>Category 4: "Policies and Procedures" — For official IT policies</li>
  </ul>
</li>
<li>For the "Troubleshooting" category, create a sub-category: "Email Issues"</li>
<li>This creates a hierarchical taxonomy: IT Support Knowledge Base > Troubleshooting > Email Issues</li>
</ol>

<h3>Exercise 3: Write a Knowledge Article</h3>
<ol>
<li>Navigate to <strong>Knowledge > Create New</strong></li>
<li>Select your "IT Support Knowledge Base" as the target</li>
<li>Fill in the article fields:
  <ul>
    <li><strong>Short Description:</strong> "How to Connect to the Corporate VPN"</li>
    <li><strong>Category:</strong> "How-To Guides"</li>
    <li><strong>Article body:</strong> Write a comprehensive article using the rich text editor:
      <ul>
        <li>Start with a brief overview of what the VPN is and when it's needed</li>
        <li>Add prerequisites (VPN client installed, valid credentials)</li>
        <li>Write numbered steps for the connection process</li>
        <li>Include a troubleshooting section for common issues</li>
        <li>Add contact information for further help</li>
      </ul>
    </li>
  </ul>
</li>
<li>Use the rich text editor formatting tools to add headings, bullet points, numbered lists, and bold text</li>
<li>Add relevant <strong>keywords</strong> in the Keywords field: "VPN, remote access, connection, network"</li>
<li>Save the article in <strong>Draft</strong> state</li>
</ol>

<h3>Exercise 4: Submit for Review and Publish</h3>
<ol>
<li>Open your draft article</li>
<li>Change the <strong>Workflow State</strong> to "Review" — this submits the article for approval</li>
<li>In a real environment, the knowledge manager would review and provide feedback</li>
<li>Since you're also the knowledge manager in your PDI, open the article and approve it</li>
<li>Change the state to <strong>"Published"</strong></li>
<li>Navigate to the <strong>Service Portal</strong> and search for your article to verify it appears in search results</li>
<li>Click through the article and note how it appears to end users</li>
</ol>

<h3>Exercise 5: Test Knowledge in Incident Context</h3>
<ol>
<li>Create a new incident with Short Description: "Cannot connect to VPN from home"</li>
<li>While on the incident form, look for the <strong>Knowledge</strong> panel or related list</li>
<li>Search for "VPN" — your published article should appear as a suggested solution</li>
<li>Click to attach the article to the incident as the resolution reference</li>
<li>This demonstrates how knowledge integrates with the incident resolution workflow</li>
</ol>

<h3>Key Takeaways</h3>
<p>Knowledge Management success depends on: (1) well-organized knowledge bases with clear categorization, (2) well-written articles that follow consistent templates and are optimized for search, (3) a maintained lifecycle that keeps content current through regular reviews, (4) integration with incident management to both suggest articles to agents and capture new knowledge from resolutions, and (5) metrics tracking to continuously improve knowledge base effectiveness and coverage.</p>`
      }
    ]
  },
  {
    id: 'w-catalog',
    title: 'Service Catalog',
    readingTime: 20,
    subtopics: [
      {
        id: 'w-catalog-lesson',
        title: 'Service Catalog Lesson',
        readingTime: 12,
        content: `<h2>Service Catalog in ServiceNow</h2>
<p>The Service Catalog is a self-service portal where users can browse and request IT services, hardware, software, and other business services. Think of it as an "Amazon-like" shopping experience for enterprise services. The catalog streamlines service delivery by providing a structured request process with automated fulfillment workflows, approval routing, and SLA tracking.</p>

<h3>Why Service Catalogs Matter</h3>
<p>Without a service catalog, users typically submit requests through email, phone calls, or generic incident forms, resulting in inconsistent information capture, unclear fulfillment processes, and poor tracking. A well-designed service catalog provides:</p>
<ul>
<li><strong>Standardized Requests:</strong> Each catalog item captures exactly the information needed for fulfillment, reducing back-and-forth communication</li>
<li><strong>Automated Workflows:</strong> Approval routing, task generation, and fulfillment processes are automated</li>
<li><strong>Cost Tracking:</strong> Items can have associated costs for chargeback or showback reporting</li>
<li><strong>SLA Management:</strong> Fulfillment timelines are defined and tracked automatically</li>
<li><strong>Self-Service Enablement:</strong> Users can request services 24/7 without contacting the service desk</li>
</ul>

<h3>Service Catalog Architecture</h3>
<p>The catalog is organized in a hierarchical structure:</p>
<ul>
<li><strong>Service Catalogs [sc_catalog]:</strong> Top-level containers. Most organizations have one or two catalogs (e.g., "Service Catalog," "Technical Catalog"). Enterprise customers may have separate catalogs per department.</li>
<li><strong>Categories [sc_category]:</strong> Organize catalog items into logical groups visible to users. Examples: "Hardware," "Software," "Access Requests," "Office Supplies." Categories can be nested for deeper organization.</li>
<li><strong>Catalog Items [sc_cat_item]:</strong> Individual services or products that users can request. Each item has a name, description, price, picture, and associated fulfillment process.</li>
<li><strong>Record Producers [sc_cat_item_producer]:</strong> Special catalog items that create records directly on a specified table when submitted. For example, a "Report a Building Issue" record producer creates a Facilities Management task.</li>
<li><strong>Order Guides [sc_cat_item_guide]:</strong> Bundle multiple catalog items into a guided ordering experience. Used for complex requests that involve multiple related items (e.g., "New Employee Setup" that bundles laptop, software, building access, and email account requests).</li>
</ul>

<h3>Catalog Item Configuration</h3>
<p>Each catalog item consists of several configuration elements:</p>

<p><strong>Variables (Form Fields):</strong></p>
<p>Variables are the form fields that capture user input during the request process. ServiceNow supports many variable types:</p>
<ul>
<li><strong>Single-Line Text:</strong> Short text input (e.g., "Employee Name")</li>
<li><strong>Multi-Line Text:</strong> Longer text area (e.g., "Business Justification")</li>
<li><strong>Select Box (Dropdown):</strong> Predefined choices (e.g., "Laptop Model: Standard, Performance, Executive")</li>
<li><strong>Checkbox / Yes-No:</strong> Boolean selections</li>
<li><strong>Reference:</strong> Lookup fields that reference another table (e.g., "Manager" referencing the User table)</li>
<li><strong>Date/Date-Time:</strong> Date picker for scheduling (e.g., "Preferred Delivery Date")</li>
<li><strong>Attachment:</strong> File upload capability</li>
<li><strong>Lookup Select Box:</strong> Multi-select from a table</li>
</ul>

<p><strong>Variable Sets:</strong></p>
<p>Variable Sets are reusable groups of variables that can be shared across multiple catalog items. For example, a "Delivery Information" variable set containing Address, Building, Floor, and Desk Number could be reused on hardware requests, furniture requests, and supply requests.</p>

<p><strong>Catalog UI Policies:</strong></p>
<p>Similar to form UI Policies, Catalog UI Policies control variable visibility, mandatory status, and read-only status based on conditions. Example: Show "Software License Key" variable only when "Software Type" is "Licensed (Bring Your Own Key)."</p>

<h3>Request Fulfillment Process</h3>
<p>When a user submits a catalog request, ServiceNow automatically creates a chain of records:</p>
<ol>
<li><strong>Request [sc_request]:</strong> The parent record representing the overall order (like an e-commerce order)</li>
<li><strong>Requested Item [sc_req_item]:</strong> One RITM per catalog item in the order. Contains the user's variable answers and tracks the item's fulfillment status</li>
<li><strong>Catalog Tasks [sc_task]:</strong> Individual tasks assigned to fulfillment teams. A single RITM can generate multiple tasks (e.g., "Procure Hardware" assigned to Procurement, "Configure Laptop" assigned to IT, "Ship to User" assigned to Logistics)</li>
</ol>

<h3>Approval Configuration</h3>
<p>Approvals can be configured at multiple levels:</p>
<ul>
<li><strong>No Approval:</strong> Request is automatically approved (for low-cost, standard items)</li>
<li><strong>Manager Approval:</strong> Requester's manager must approve before fulfillment begins</li>
<li><strong>Group Approval:</strong> A specific group must approve (e.g., Finance approval for items over $5,000)</li>
<li><strong>Multi-Level Approval:</strong> Sequential approvals from multiple authorities</li>
<li><strong>Condition-Based Approval:</strong> Approval requirements that change based on request details (e.g., items over a cost threshold require additional approval)</li>
</ul>`
      },
      {
        id: 'w-catalog-task',
        title: 'Service Catalog Task',
        readingTime: 8,
        content: `<h2>Hands-On: Service Catalog</h2>
<p>This exercise guides you through creating a complete catalog item with variables, workflow, and fulfillment — from design through testing as an end user.</p>

<h3>Exercise 1: Explore the Existing Catalog</h3>
<ol>
<li>Navigate to <strong>Self-Service > Service Catalog</strong> in the All menu</li>
<li>Browse the available categories — these represent the user-facing catalog experience</li>
<li>Click into a category (e.g., "Hardware") and explore available items</li>
<li>Open any existing catalog item and examine:
  <ul>
    <li>How the description and picture are displayed to users</li>
    <li>The variables (form fields) the user must fill in</li>
    <li>Any dynamic behavior (fields showing/hiding based on selections)</li>
  </ul>
</li>
<li>Submit a test request and observe the confirmation page</li>
</ol>

<h3>Exercise 2: Create a New Catalog Item</h3>
<ol>
<li>Navigate to <strong>Service Catalog > Catalog Definitions > Maintain Items</strong></li>
<li>Click <strong>"New"</strong> to create a new item</li>
<li>Fill in the basic information:
  <ul>
    <li><strong>Name:</strong> "Request a New Laptop"</li>
    <li><strong>Short Description:</strong> "Order a new laptop for business use"</li>
    <li><strong>Description:</strong> Write a detailed description explaining what's included, delivery timeline, and who should use this item</li>
    <li><strong>Category:</strong> Select "Hardware" (or create one)</li>
    <li><strong>Price:</strong> Set a representative price (e.g., $1,200)</li>
    <li><strong>Picture:</strong> Upload a laptop image (optional)</li>
  </ul>
</li>
<li>Click <strong>"Submit"</strong> to create the item</li>
</ol>

<h3>Exercise 3: Add Variables to the Catalog Item</h3>
<ol>
<li>Open your "Request a New Laptop" item</li>
<li>Scroll to the <strong>"Variables"</strong> related list and click <strong>"New"</strong></li>
<li>Create these variables:
  <ul>
    <li><strong>Laptop Type</strong> (Select Box): Options: "Standard - Windows," "Performance - Windows," "MacBook Pro." Set as mandatory.</li>
    <li><strong>Business Justification</strong> (Multi-Line Text): Why is this laptop needed? Set as mandatory.</li>
    <li><strong>Preferred Delivery Date</strong> (Date): When would you like it? Add a help text: "Please allow at least 5 business days for delivery."</li>
    <li><strong>Additional Software Needed</strong> (Checkbox): "Do you need additional software installed?"</li>
    <li><strong>Software List</strong> (Multi-Line Text): Visible only when "Additional Software" is checked. Use a Catalog UI Policy to show/hide this field.</li>
  </ul>
</li>
<li>Set the variable order to control the display sequence on the request form</li>
</ol>

<h3>Exercise 4: Configure a Catalog UI Policy</h3>
<ol>
<li>On your catalog item, navigate to the <strong>"Catalog UI Policies"</strong> related list</li>
<li>Click <strong>"New"</strong> to create a policy</li>
<li>Set the condition: <strong>"Additional Software Needed" is "true"</strong></li>
<li>In the UI Policy Actions, set <strong>"Software List"</strong> to <strong>Visible = true</strong></li>
<li>When "Additional Software Needed" is not checked, the Software List field will be hidden — providing a cleaner form experience</li>
</ol>

<h3>Exercise 5: Test as an End User</h3>
<ol>
<li>Navigate to the <strong>Service Portal</strong> (or Self-Service > Service Catalog)</li>
<li>Find your "Request a New Laptop" item</li>
<li>Fill in all the variables and submit the request</li>
<li>Check the confirmation page — note the Request number (REQ)</li>
<li>Navigate to <strong>Self-Service > My Requests</strong> to see your submitted request</li>
<li>Open the request and find the Requested Item (RITM) — this tracks your item's fulfillment</li>
<li>If catalog tasks were generated, find them in the RITM's related list</li>
</ol>

<h3>Key Takeaways</h3>
<p>Service Catalog design should always prioritize the end user experience. Key principles: (1) use clear, descriptive names and descriptions that non-technical users understand, (2) only ask for information that's genuinely needed for fulfillment — every extra field reduces completion rates, (3) use UI Policies to show/hide fields dynamically to keep forms clean, (4) configure appropriate approval workflows based on cost and risk, and (5) test every catalog item from the end user's perspective before publishing.</p>`
      }
    ]
  },
  {
    id: 'w-virtual-agent',
    title: 'Virtual Agent',
    readingTime: 20,
    subtopics: [
      {
        id: 'w-va-lesson',
        title: 'Virtual Agent Lesson',
        readingTime: 12,
        content: `<h2>Virtual Agent in ServiceNow</h2>
<p>ServiceNow Virtual Agent is a conversational AI-powered chatbot that provides automated assistance to users through natural language interactions. It handles common requests, answers questions, performs actions, and when necessary, seamlessly transfers conversations to human agents. Virtual Agent is a cornerstone of modern self-service strategies, capable of significantly reducing ticket volumes, improving response times from hours to seconds, and providing 24/7 support availability.</p>

<h3>How Virtual Agent Works</h3>
<p>Virtual Agent processes user messages through several layers:</p>
<ol>
<li><strong>Natural Language Understanding (NLU):</strong> The NLU engine interprets the user's message to determine their <strong>intent</strong> — what the user is trying to accomplish. For example, "I can't log in" maps to the "Password Reset" intent. ServiceNow uses advanced NLU models that can understand variations, synonyms, and even misspellings.</li>
<li><strong>Intent Matching:</strong> The determined intent is matched to a <strong>Topic</strong> — a predefined conversation flow designed to handle that specific request type.</li>
<li><strong>Conversation Execution:</strong> The Topic's conversation flow guides the interaction through a series of prompts, actions, and decisions until the user's request is resolved or escalated.</li>
<li><strong>Entity Extraction:</strong> During conversation, the NLU engine extracts specific data points (entities) from user messages. For example, from "My laptop screen is cracked," it extracts "laptop" as the device and "screen cracked" as the issue type.</li>
</ol>

<h3>Topics (Conversation Flows)</h3>
<p>Topics are the building blocks of Virtual Agent. Each topic handles a specific user intent and is built using the <strong>Topic Designer</strong> — a visual, flow-chart-style editor. Topics consist of:</p>
<ul>
<li><strong>User Prompts:</strong> Questions the bot asks the user to gather information (e.g., "What device are you having trouble with?")</li>
<li><strong>Bot Responses:</strong> Messages or cards the bot sends (text, links, images, or interactive elements)</li>
<li><strong>Decisions (Conditional Branching):</strong> Logic that routes the conversation based on user responses or system data</li>
<li><strong>Actions:</strong> System operations like creating an incident, searching knowledge, updating a record, or calling a Flow Designer flow</li>
<li><strong>Utilities:</strong> Sub-conversations (nested topics), scripts, and integrations for complex logic</li>
<li><strong>Agent Handoff:</strong> Transfers the conversation to a live agent when the bot cannot resolve the issue, including full conversation context</li>
</ul>

<p><strong>Pre-Built Topics:</strong></p>
<p>ServiceNow provides dozens of ready-to-use topics for common IT scenarios:</p>
<ul>
<li>Password Reset / Unlock Account</li>
<li>Create an Incident</li>
<li>Check Incident Status</li>
<li>Order from Service Catalog</li>
<li>Search Knowledge Base</li>
<li>VPN Help</li>
<li>Software Installation Request</li>
</ul>

<h3>Deployment Channels</h3>
<p>Virtual Agent can be deployed across multiple channels for maximum reach:</p>
<ul>
<li><strong>Service Portal:</strong> Embedded chat widget on the self-service portal — the most common deployment</li>
<li><strong>Microsoft Teams:</strong> Direct integration allows users to chat with the bot in their daily collaboration tool</li>
<li><strong>Slack:</strong> Similar to Teams integration for Slack-based organizations</li>
<li><strong>Mobile App:</strong> Native ServiceNow mobile application with built-in chat</li>
<li><strong>Employee Center:</strong> Integrated into the unified employee portal</li>
</ul>

<h3>NLU Model Training</h3>
<p>The NLU model's effectiveness depends on training data quality. As an administrator, you'll manage:</p>
<ul>
<li><strong>Training Utterances:</strong> Example phrases for each intent. More diverse training examples improve recognition accuracy. For the "Password Reset" intent, examples might include: "forgot my password," "can't log in," "need to change my password," "locked out of my account," "password expired."</li>
<li><strong>Model Testing:</strong> Test your NLU model with sample phrases to verify correct intent matching before deployment</li>
<li><strong>Continuous Improvement:</strong> Review unmatched utterances (messages the bot couldn't classify) to identify gaps and add new training data</li>
</ul>

<h3>Analytics and Optimization</h3>
<p>Virtual Agent provides rich analytics for monitoring and improving performance:</p>
<ul>
<li><strong>Conversation Volume:</strong> Total conversations initiated, completed, and abandoned</li>
<li><strong>Resolution Rate:</strong> Percentage of conversations resolved without human escalation</li>
<li><strong>Topic Distribution:</strong> Which topics are triggered most frequently — guides investment in improving high-volume topics</li>
<li><strong>Escalation Rate:</strong> Percentage transferred to live agents — high rates indicate topics need improvement</li>
<li><strong>User Satisfaction:</strong> Post-conversation CSAT ratings</li>
<li><strong>Unmatched Utterances:</strong> Messages the bot couldn't classify — goldmine for NLU improvement</li>
</ul>`
      },
      {
        id: 'w-va-task',
        title: 'Virtual Agent Task',
        readingTime: 8,
        content: `<h2>Hands-On: Virtual Agent</h2>
<p>This exercise explores Virtual Agent capabilities, helps you understand conversation design, and teaches you how to monitor and improve bot performance.</p>

<h3>Exercise 1: Test Virtual Agent as an End User</h3>
<ol>
<li>Navigate to the <strong>Service Portal</strong> in your instance</li>
<li>Look for the <strong>Virtual Agent chat widget</strong> — usually a chat icon in the bottom-right corner</li>
<li>Start a conversation by typing <strong>"I need help"</strong></li>
<li>The bot should respond with options or ask clarifying questions</li>
<li>Try these common requests and observe how the bot handles each:
  <ul>
    <li>"I forgot my password" — should trigger the Password Reset topic</li>
    <li>"I need a new laptop" — should suggest the Service Catalog or create a request</li>
    <li>"How do I connect to VPN?" — should search the Knowledge Base</li>
    <li>"Create an incident" — should guide you through incident creation</li>
    <li>"What's the status of my incident?" — should look up your recent incidents</li>
  </ul>
</li>
<li>Note how each conversation follows a structured flow with prompts and actions</li>
</ol>

<h3>Exercise 2: Explore the Topic Designer</h3>
<ol>
<li>Navigate to <strong>Virtual Agent > Designer</strong></li>
<li>Browse the list of available topics — note which are active and which are inactive</li>
<li>Open a pre-built topic such as <strong>"Create Incident"</strong></li>
<li>Examine the visual flow structure:
  <ul>
    <li>Identify <strong>User Prompts</strong> — nodes where the bot asks questions</li>
    <li>Identify <strong>Bot Responses</strong> — nodes where the bot provides information</li>
    <li>Identify <strong>Decision nodes</strong> — branching points based on user input</li>
    <li>Identify <strong>Action nodes</strong> — where the bot performs system operations (create record, search knowledge)</li>
    <li>Identify <strong>End nodes</strong> — where the conversation concludes</li>
  </ul>
</li>
<li>Click individual nodes to see their configuration details</li>
</ol>

<h3>Exercise 3: Review NLU Configuration</h3>
<ol>
<li>Navigate to <strong>Virtual Agent > NLU</strong> (or NLU Workbench)</li>
<li>Explore the intent definitions and their associated training utterances</li>
<li>Select an intent like "Password Reset" and review:
  <ul>
    <li>How many training utterances are defined</li>
    <li>The variety of phrases covered (formal, informal, abbreviated)</li>
    <li>Any entities associated with the intent</li>
  </ul>
</li>
<li>Use the <strong>"Test Model"</strong> feature to enter custom phrases and see which intent they match</li>
<li>Try phrases that are slightly different from training data to test the model's generalization ability</li>
<li>Try a completely unrelated phrase to see how the bot handles unrecognized inputs</li>
</ol>

<h3>Exercise 4: Review Virtual Agent Analytics</h3>
<ol>
<li>Navigate to <strong>Virtual Agent > Analytics</strong> or the VA Analytics dashboard</li>
<li>Review the key metrics:
  <ul>
    <li>Total conversation volume over the past 30 days</li>
    <li>Resolution rate (conversations resolved without human agent)</li>
    <li>Most popular topics (which intents are triggered most)</li>
    <li>Escalation rate and reasons for escalation</li>
  </ul>
</li>
<li>Find the <strong>"Unmatched Utterances"</strong> report — these are messages users sent that the bot couldn't classify</li>
<li>Review unmatched utterances to identify patterns — these represent gaps in your NLU training that should be addressed</li>
</ol>

<h3>Best Practices for Virtual Agent</h3>
<ul>
<li><strong>Start simple:</strong> Begin with 5-10 high-volume, well-defined use cases. It's better to have a few topics that work perfectly than many that work poorly.</li>
<li><strong>Write natural responses:</strong> Bot responses should sound helpful and conversational, not robotic. Use the organization's brand voice.</li>
<li><strong>Always provide an exit:</strong> Every conversation path should include an option to reach a human agent. Users should never feel trapped in a conversation with no way out.</li>
<li><strong>Train continuously:</strong> Regularly review unmatched utterances and add new training phrases. NLU accuracy improves over time with more diverse training data.</li>
<li><strong>Measure and iterate:</strong> Review analytics weekly. Focus improvement efforts on high-volume topics with low resolution rates.</li>
<li><strong>Test across channels:</strong> If deployed on multiple channels (Portal, Teams, Slack), test on each — the experience may differ.</li>
</ul>`
      }
    ]
  },
  {
    id: 'w-summary',
    title: 'Course Summary',
    readingTime: 8,
    subtopics: [
      {
        id: 'w-resources',
        title: 'Additional ServiceNow Resources',
        readingTime: 5,
        content: `<h2>Additional ServiceNow Resources</h2>
<p>Congratulations on completing the "Welcome to ServiceNow" module! You now have a solid foundation in the ServiceNow platform. To continue growing your skills and stay current with platform developments, here is a comprehensive guide to the ServiceNow resource ecosystem.</p>

<h3>Official Learning and Certification Resources</h3>
<ul>
<li><strong>Now Learning (nowlearning.servicenow.com):</strong> ServiceNow's official training platform. Offers instructor-led courses, on-demand learning paths, hands-on labs, and certification preparation. Many courses are free, with paid options for advanced certifications. This should be your primary learning resource.</li>
<li><strong>Certification Program:</strong> ServiceNow offers a comprehensive certification program with multiple levels:
  <ul>
    <li><strong>Certified System Administrator (CSA):</strong> The foundational certification. Validates core platform knowledge. Required before pursuing any specialized certification.</li>
    <li><strong>Certified Implementation Specialist (CIS):</strong> Specialization certifications in ITSM, CSM, HR, ITOM, Risk and Compliance, and more.</li>
    <li><strong>Certified Application Developer (CAD):</strong> For those focused on building applications on the platform.</li>
    <li><strong>Micro-Certifications:</strong> Shorter, focused certifications on specific topics like Flow Designer, Virtual Agent, or Performance Analytics.</li>
  </ul>
</li>
</ul>

<h3>Developer and Technical Resources</h3>
<ul>
<li><strong>Developer Site (developer.servicenow.com):</strong> Essential resource for technical professionals. Provides:
  <ul>
    <li>Free Personal Developer Instances (PDIs) for hands-on practice</li>
    <li>Comprehensive API documentation (REST, SOAP, GlideRecord)</li>
    <li>Code snippets and sample applications</li>
    <li>Developer blog with technical deep-dives</li>
    <li>Integration resources and partner APIs</li>
  </ul>
</li>
<li><strong>ServiceNow Documentation (docs.servicenow.com):</strong> The definitive reference for all platform features, organized by product, feature, and release version. Includes installation guides, configuration instructions, and API references.</li>
<li><strong>ServiceNow Share:</strong> A marketplace of free, community-contributed applications, widgets, update sets, and integrations. Browse existing solutions before building from scratch.</li>
</ul>

<h3>Community and Networking</h3>
<ul>
<li><strong>ServiceNow Community (community.servicenow.com):</strong> Active forums with over 500,000 members. Post questions, share knowledge, and learn from experienced practitioners. The community is organized by product area, making it easy to find relevant discussions.</li>
<li><strong>ServiceNow User Groups (SNUGs):</strong> Local, in-person meetups in cities worldwide. SNUGs provide networking opportunities, guest speakers, product previews, and knowledge sharing. Find your local SNUG on the community site.</li>
<li><strong>Knowledge Conference:</strong> ServiceNow's annual conference (held in Las Vegas and online). Features keynotes, hands-on labs, networking, certification opportunities, and product announcements. CreatorCon labs are particularly valuable for hands-on learning.</li>
<li><strong>LinkedIn Groups:</strong> Several active ServiceNow LinkedIn groups for professional networking and job opportunities.</li>
</ul>

<h3>Staying Current</h3>
<p>ServiceNow evolves rapidly with bi-annual major releases. Stay current by:</p>
<ul>
<li>Subscribing to ServiceNow release notes for your product areas</li>
<li>Following the ServiceNow blog for feature announcements</li>
<li>Attending webinars and virtual events for new capabilities</li>
<li>Upgrading your PDI to the latest release for hands-on exploration</li>
<li>Joining the Early Availability Program for preview access to upcoming features</li>
</ul>`
      },
      {
        id: 'w-whats-next',
        title: "What's Next?",
        readingTime: 3,
        content: `<h2>What's Next?</h2>
<p>You've completed the "Welcome to ServiceNow" module and built a solid foundation in the ServiceNow platform. Here's your roadmap for continuing your learning journey.</p>

<h3>Immediate Next Steps</h3>
<ol>
<li><strong>Continue to SNAF Modules:</strong> Proceed to the "ServiceNow Administration Fundamentals" section. These 9 modules take you deeper into administration topics including data management, security, workflow automation, update sets, and more. This is the knowledge you need for the CSA certification exam.</li>
<li><strong>Complete the Knowledge Check:</strong> Take the Welcome module quiz below to test your understanding of the concepts covered. Aim for at least 70% — review any topics where you score lower.</li>
<li><strong>Practice in Your PDI:</strong> If you haven't already, request a Personal Developer Instance at developer.servicenow.com. Hands-on practice is the most effective way to solidify your learning. Spend at least 30 minutes per day exploring features in your PDI.</li>
</ol>

<h3>Building Your Study Plan</h3>
<p>For the most effective learning experience, we recommend this approach:</p>
<ul>
<li><strong>Study sessions:</strong> Plan 1-2 hour study sessions rather than marathon cramming. Consistent daily practice is more effective than occasional long sessions.</li>
<li><strong>Hands-on ratio:</strong> For every 30 minutes of reading, spend 30 minutes practicing in your PDI. The hands-on exercises in each module are designed to reinforce the concepts you read about.</li>
<li><strong>Quiz strategy:</strong> Take each module's knowledge check immediately after completing the module. If you score below 70%, re-read the sections you missed before moving on.</li>
<li><strong>Bookmark strategy:</strong> Use the bookmark feature to mark sections you found challenging or that you want to revisit before the certification exam.</li>
</ul>

<h3>Preparing for CSA Certification</h3>
<p>The Certified System Administrator exam covers all topics in both the Welcome module and SNAF modules. Key exam preparation tips:</p>
<ul>
<li>Complete all 9 SNAF modules and their knowledge checks</li>
<li>Complete the Capstone Project (SNAF Module 9) to validate your hands-on skills</li>
<li>Review the official CSA exam guide on Now Learning</li>
<li>Take practice exams to identify weak areas</li>
<li>Schedule your exam when you consistently score above 80% on practice tests</li>
</ul>

<p>Good luck with the rest of your training! The ServiceNow Administration Fundamentals modules are waiting for you in the sidebar.</p>`
      }
    ],
  },
];

export const welcomeQuiz = {
  id: 'w-quiz',
  title: 'Welcome to ServiceNow - Knowledge Check',
  questions: [
    {
      id: 'wq1',
      type: 'mcq',
      question: 'What is the modern user interface framework in ServiceNow called?',
      options: ['UI16', 'Next Experience', 'Classic UI', 'Seismic UI'],
      correctAnswer: 1,
    },
    {
      id: 'wq2',
      type: 'truefalse',
      question: 'The Task table is the parent table for Incidents, Problems, and Change Requests in ServiceNow.',
      correctAnswer: true,
    },
    {
      id: 'wq3',
      type: 'mcq',
      question: 'Which component of ServiceNow allows users to request services through a self-service portal?',
      options: ['Knowledge Base', 'Service Catalog', 'Virtual Agent', 'Platform Analytics'],
      correctAnswer: 1,
    },
    {
      id: 'wq4',
      type: 'truefalse',
      question: 'ServiceNow operates on a multi-instance, single-tenant architecture where customers share a database.',
      correctAnswer: false,
    },
    {
      id: 'wq5',
      type: 'mcq',
      question: 'What tool in ServiceNow provides conversational AI chatbot capabilities?',
      options: ['Flow Designer', 'Virtual Agent', 'IntegrationHub', 'Report Builder'],
      correctAnswer: 1,
    },
    {
      id: 'wq6',
      type: 'mcq',
      question: 'How is Priority typically calculated in ServiceNow task records?',
      options: ['Manually set by the agent', 'From Impact and Urgency using lookup rules', 'Based on the caller\'s VIP status', 'Automatically by AI'],
      correctAnswer: 1,
    },
    {
      id: 'wq7',
      type: 'truefalse',
      question: 'Dot-walking in ServiceNow filters allows you to filter records based on fields in related tables.',
      correctAnswer: true,
    },
    {
      id: 'wq8',
      type: 'mcq',
      question: 'What is the correct lifecycle order for a knowledge article?',
      options: ['Published > Draft > Review > Retired', 'Draft > Review > Published > Retired', 'Review > Draft > Published > Closed', 'Draft > Published > Review > Retired'],
      correctAnswer: 1,
    },
  ],
};
